import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Property } from '@/modules/Property/interfaces/property'
import { showErrors } from '@/utils/snack'
import { dateTimeToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import PropertyDialog from '@/modules/Property/scripts/property-dialog'
import { getProperties } from '@/modules/Property/gql/property.gql'
import { getCaseById } from '@/modules/Case/gql/case.gql'
import { getPolicyById } from '@/modules/Policy/gql/policy.gql'
import { getQuote } from '@/modules/Quote/gql/quote.gql'
import { Case } from '@/modules/Case/interfaces/case'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { Quote } from '@/modules/Quote/interfaces/quote'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField
  }
})
export default class PropertyList extends mixins(PropertyDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Properties'
    }
  ]
  searchVal = ''

  properties: Property[] = []
  policy: Policy | null = null
  quote: Quote | null = null
  caseInfo: Case | null = null

  tableColumn = [
    { field: 'address', title: 'Address', slots: { default: 'address_default' }, resizable: true, sortable: true },
    { field: 'city', title: 'City', resizable: true, sortable: true },
    { field: 'state', title: 'State', resizable: true, sortable: true },
    { field: 'totalLivingArea',
      title: 'Total Living Area',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', (JSON.parse(row.additionalInfoStr)).totalLivingArea || '-')
        ]
      }
      },
      resizable: true
    },
    { field: 'yearBuilt',
      title: 'Year Built',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', (JSON.parse(row.additionalInfoStr)).yearBuilt || '-')
        ]
      }
      },
      resizable: true
    },
    { title: 'Zip Code',
      field: 'zipCode',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.zipCode ? row.zipCode : '-')
        ]
      }
      },
      sortable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 90 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    address: 'ADDRESS',
    city: 'CITY',
    zipCode: 'ZIP_CODE'
  }

  dateTimeToDisplay = dateTimeToDisplay

  mounted () {
    this.onResize()
    this.getList()
  }

  async getList () {
    const { params } = this.$route
    const { policyId, quoteId, caseId } = params

    if (policyId) {
      await this.getPolicy(+policyId)
    } else if (quoteId) {
      await this.getQuote(+quoteId)
    } else if (caseId) {
      await this.getCase(+caseId)
    }
    return this.getProperties()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    this.routeTo('PropertyDetailDetail', {
      id: item.id
    })
  }

  async getCase (caseId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getCaseById.inquiryName} (Case ID: ${result.data.getCaseById.id})`,
          to: {
            name: 'CaseDetailRelated',
            params: {
              id: result.data.getCaseById.id
            }
          }
        })
      }
      this.properties = result.data.getCaseById.properties
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.properties.length
      this.caseInfo = result.data.getCaseById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getPolicy (policyId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicyById,
        variables: {
          policyId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getPolicyById.policyName} (Policy ID: ${result.data.getPolicyById.id})`,
          to: {
            name: 'PolicyDetailRelated',
            params: {
              id: result.data.getPolicyById.id
            }
          }
        })
      }
      this.properties = result.data.getPolicyById.properties
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.properties.length
      this.policy = result.data.getPolicyById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getQuote (quoteId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getQuote,
        variables: {
          quoteId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getQuote.name} (Quote ID: ${result.data.getQuote.id})`,
          to: {
            name: 'QuoteDetailRelated',
            params: {
              id: result.data.getQuote.id
            }
          }
        })
      }
      this.properties = result.data.getQuote.properties
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.properties.length
      this.quote = result.data.getQuote
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getProperties () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    const policyId = Number(this.$route.params.policyId)
    const quoteId = Number(this.$route.params.quoteId)
    const caseId = Number(this.$route.params.caseId)
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getProperties,
        variables: {
          filter: {
            address: searchVal,
            policyId,
            quoteId,
            caseId
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.properties = result.data.getProperties.properties
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getProperties.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async deletePropConfirm () {
    await this.deletePropPost()
    this.getProperties()
  }

  refreshList () {
    this.searchVal = ''
    this.getProperties()
  }

  routeToRelationship (e: Event, item: any) {
    e.preventDefault()
    e.stopPropagation()
    this.routeTo('PropertyContactRelationDetail', {
      id: item.relation.id
    })
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getProperties()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getProperties()
  }
}
